import { Suspense, lazy, useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/auth";
import { BannerProvider } from "./context/BannerContext";
import { SnackbarProvider } from "./context/SnackbarContext";
import toast, { useToasterStore } from "react-hot-toast";
import { isOnline } from "./utils";
import { CloudOffIcon } from "lucide-react";
import Header from "./components/Header";
import PaddedLayout from "./components/layouts/PaddedLayout";
import ProtectedRoute from "./ProtectedRoute";
import MainLoader from "./components/shared/MainLoader";

import { checkAndAwardAchievements } from "./store/user";
import CartSummary from "./components/Cart/CartSummary";
import CookieConsent from "./components/CookieConsent";
import { SiteStructuredData } from "./SEO";
import { trackPageView } from "./lib/analytics";

// The landing page is the most common entry point, so it stays in the initial bundle
// to avoid a chunk round-trip before first paint. Everything else is split out — the
// admin panel, the course editor and the lesson player pull in the heaviest
// dependencies (TipTap, xlsx, pdf-lib, syntax highlighting) and no visitor should
// download them just to read the homepage.
import LandingPage from "./pages/Home";

const AuthPage = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const PaymentCallback = lazy(() => import("./pages/PaymentCallback"));
const EnrolledPage = lazy(() => import("./pages/EnrolledPage"));
const Profile = lazy(() => import("./pages/Profile"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const CoursePage = lazy(() => import("./pages/Courses/Course"));
const CoursesListPage = lazy(() => import("./pages/CoursesPage"));
const CourseViewPage = lazy(() => import("./pages/CourseViewPage"));
const NotFoundPage = lazy(() => import("./pages/NotfoundPage"));
const ScholarshipsPage = lazy(() => import("./pages/ScholarshipsPage"));
const ScholarshipApplicationPage = lazy(() => import("./pages/ScholarshipApplicationPage"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicy"));
const CartPage = lazy(() => import("./pages/CartPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const EventDetailPage = lazy(() => import("./pages/EventDetailPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const PartnershipPage = lazy(() => import("./pages/PartnershipPage"));
const TeamsPage = lazy(() => import("./pages/TeamsPage"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const CampaignLandingPage = lazy(() => import("./pages/CampaignLandingPage"));
const FreeResourcesIndexPage = lazy(() => import("./pages/FreeResourcesIndexPage"));
const FreeResourcePage = lazy(() => import("./pages/FreeResourcePage"));
const FreeResourceThankYouPage = lazy(() => import("./pages/FreeResourceThankYouPage"));
const CertificatePage = lazy(() => import("./pages/CertificatePage"));
const CertificateVerifyPage = lazy(() => import("./pages/CertificateVerifyPage"));

// The partner programme. `/r/:code` is the link everybody actually shares, so it
// is kept as short as a route can be — it gets read aloud, printed on flyers and
// re-typed from screenshots.
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const PartnerDashboardPage = lazy(() => import("./pages/PartnerDashboardPage"));
const ReferralLandingPage = lazy(() => import("./pages/ReferralLandingPage"));

// Admin — never loaded for signed-out visitors.
const PersonalOfferPage = lazy(() => import("./pages/PersonalOfferPage"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const ActivitiesPage = lazy(() => import("./pages/admin/pages/Activities"));
const CreateCoursePage = lazy(() => import("./pages/admin/pages/CreateCourse"));
const ViewCoursesPage = lazy(() => import("./pages/admin/pages/ViewCourses"));
const CreateScholarshipPage = lazy(() => import("./pages/admin/pages/CreateScholarship"));
const ViewScholarshipsPage = lazy(() => import("./pages/admin/pages/ViewScholarships"));
const ManageApplicantsPage = lazy(() => import("./pages/admin/pages/ManageApplicants"));
const ViewUsersPage = lazy(() => import("./pages/admin/pages/ViewUsers"));
const UserProfilePage = lazy(() => import("./pages/admin/pages/UserProfile"));
const AdminScholarshipApplicationsPage = lazy(() => import("./pages/admin/pages/AdminScholarshipApplicationsPage"));
const CertificateRequestsPage = lazy(() => import("./pages/admin/pages/CertificateRequestsPage"));
const AdminCoursePreviewPage = lazy(() => import("./pages/admin/pages/CoursePreviewPage"));
const NotifyPage = lazy(() => import("./pages/admin/pages/NotifyPage"));
const ManageNewsletter = lazy(() => import("./pages/admin/pages/ManageNewsletter"));
const AddInstructorPage = lazy(() => import("./pages/admin/pages/AddInstructor"));
const ViewInstructorsPage = lazy(() => import("./pages/admin/pages/ViewInstructors"));
const ManageTestimonialsPage = lazy(() => import("./pages/admin/pages/ManageTestimonials"));
const ManageEvents = lazy(() => import("./pages/admin/pages/ManageEvents"));
const PromoCodeManagementPage = lazy(() => import("./pages/admin/pages/PromoCodeManagementPage"));
const PersonalOffersPage = lazy(() => import("./pages/admin/pages/PersonalOffersPage"));
const ContentCalendarPage = lazy(() => import("./pages/admin/pages/ContentCalendarPage"));
const ConversionPage = lazy(() => import("./pages/admin/pages/ConversionPage"));
const PaidAdsPage = lazy(() => import("./pages/admin/pages/PaidAdsPage"));
const ManageLeads = lazy(() => import("./pages/admin/pages/ManageLeads"));
const NurturePage = lazy(() => import("./pages/admin/pages/NurturePage"));
const SalesAssistantPage = lazy(() => import("./pages/admin/pages/SalesAssistantPage"));
const SalesAnswersPage = lazy(() => import("./pages/admin/pages/SalesAnswersPage"));
const CallDeskPage = lazy(() => import("./pages/admin/pages/CallDeskPage"));
const CallGroupsPage = lazy(() => import("./pages/admin/pages/CallGroupsPage"));
const WhatsappInboxPage = lazy(() => import("./pages/admin/pages/WhatsappInboxPage"));
const EnrolmentsPage = lazy(() => import("./pages/admin/pages/EnrolmentsPage"));
const AbandonedCheckoutsPage = lazy(() => import("./pages/admin/pages/AbandonedCheckoutsPage"));
const AdminHome = lazy(() => import("./pages/admin/AdminHome"));
const ManageTeam = lazy(() => import("./pages/admin/pages/ManageTeam"));
const ManagePitchDeck = lazy(() => import("./pages/admin/pages/ManagePitchDeck"));
const ManageBanner = lazy(() => import("./pages/admin/pages/ManageBanner"));
const ManageBlog = lazy(() => import("./pages/admin/pages/ManageBlog"));
const BlogEditor = lazy(() => import("./pages/admin/pages/BlogEditor"));
const ManagePages = lazy(() => import("./pages/admin/pages/ManagePages"));
const PageEditor = lazy(() => import("./pages/admin/pages/PageEditor"));
const ManageComments = lazy(() => import("./pages/admin/pages/ManageComments"));
const MediaLibrary = lazy(() => import("./pages/admin/pages/MediaLibrary"));
const ManageAffiliates = lazy(() => import("./pages/admin/pages/ManageAffiliates"));

// Route guards stay eager: they gate the lazy routes and are tiny.
import AdminProtectedRoute from "./pages/admin/AdminProtectedRoute";
import { PERMISSIONS } from "./types";

const App = () => {
  const { pathname } = useLocation();
  const { toasts } = useToasterStore();

  useEffect(() => {
    const intervalId = setInterval(() => {
      checkAndAwardAchievements();
    }, 1000 * 60 * 5);

    return () => clearInterval(intervalId);
  }, []);

  const TOAST_LIMIT = 2;
  useEffect(() => {
    toasts
      .filter((t) => t.visible)
      .filter((_, i) => i >= TOAST_LIMIT)
      .forEach((t) => toast.dismiss(t.id));
  }, [toasts]);

  useEffect(() => {
    if (!isOnline) {
      toast("You are currently offline!", {
        style: {
          backgroundColor: "gray",
          color: "#fff",
        },
        icon: <CloudOffIcon />,
      });
    }
  }, [pathname]);

  // Jump, don't glide: smooth-scrolling every route change delays the moment the
  // new page is readable, and reads as lag on a slow phone.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // The one place a page view is recorded. A client-rendered router would
  // otherwise only ever report the URL the visitor first arrived on.
  // No title is passed: at this point the new route's lazy chunk has not
  // mounted, so `document.title` still holds the previous page's value.
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);


  const cartpageRegex =
    /^\/(biller|discuss-room|chat|profile|admin|auth|cart|onboarding|password|payment|verify-email)(\/.*)?$/;

  return (
    <AuthProvider>
      <BannerProvider>
        <ThemeProvider>
          <SnackbarProvider>
            <Suspense fallback={<MainLoader />}>
              {!cartpageRegex.test(pathname) && <Header />}
              {/* The header is fixed, so the routed content is offset by its height.
                  This sits next to the condition that renders the header so the two
                  cannot drift apart — previously only PaddedLayout compensated, and
                  every route outside it rendered underneath the header. */}
              <div className={cartpageRegex.test(pathname) ? undefined : "pt-16"}>
              <Suspense fallback={<MainLoader />}>
                <Routes>
                  <Route path="/auth" element={<AuthPage />} />

                  {/* Account recovery. ForgotPassword.tsx existed but was routed
                      nowhere, and no page consumed the reset token at all — so a
                      forgotten password meant a lost account and lost enrolments. */}
                  <Route path="/password/forgot" element={<ForgotPassword />} />
                  <Route path="/password/reset/:token" element={<ResetPassword />} />
                  <Route path="/verify-email/:token" element={<VerifyEmail />} />

                  {/* Where Paystack returns a buyer after a redirect payment —
                      bank transfer and USSD, which leave the site entirely. Not
                      behind ProtectedRoute: the session can lapse during a
                      redirect, and bouncing a paying customer to a login screen
                      is the worst possible moment to do it. */}
                  <Route path="/payment/callback" element={<PaymentCallback />} />

                  {/* Where a confirmed payment lands. Both checkouts end here —
                      the inline card flow directly, and the redirect flow via
                      /payment/callback once it has settled. It replaced
                      `navigate('/courses')`, which answered a new buyer's
                      questions with a catalogue and put fifty-nine other
                      courses between them and their first lesson.

                      Deliberately outside ProtectedRoute for the same reason
                      the callback is: a session can lapse during a redirect,
                      and bouncing a paying customer to a login screen is the
                      worst possible moment to do it. The page reads the
                      learner's own enrolments, so a signed-out visitor simply
                      sees the "still settling" state rather than anyone
                      else's purchase. */}
                  <Route path="/enrolled" element={<EnrolledPage />} />

                  {/* The partner programme. `/partners` is public and indexable;
                      the dashboard is the partner's own and is not. `/r/:code`
                      is the referral link itself — it resolves the code, records
                      the click and moves the visitor on. */}
                  <Route path="/partners" element={<PartnersPage />} />
                  <Route
                    path="/partners/dashboard"
                    element={<ProtectedRoute><PartnerDashboardPage /></ProtectedRoute>}
                  />
                  <Route path="/r/:code" element={<ReferralLandingPage />} />

                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

                  <Route path="/" element={<LandingPage />} />

                  {/* Campaign landing pages. One route serves every advertisement;
                      see marketing/campaigns.ts. */}
                  <Route path="/lp/:slug" element={<CampaignLandingPage />} />

                  {/* Free-resource lead capture. One route serves all five packs;
                      see marketing/resources.ts. Unlike /lp/, these are indexable:
                      they offer something that exists nowhere else on the site. */}
                  <Route path="/free" element={<FreeResourcesIndexPage />} />
                  <Route path="/free/:slug" element={<FreeResourcePage />} />
                  <Route path="/free/:slug/thank-you" element={<FreeResourceThankYouPage />} />


                  <Route path="/scholarships" element={<ScholarshipsPage />} />
                  <Route
                    path="/scholarships/:scholarshipId/apply"
                    element={
                      <ProtectedRoute>
                        <ScholarshipApplicationPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    element={

                      <PaddedLayout />

                    }
                  >

                    <Route path="/profile/:id" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

                    {/* The interests picker. It was written, finished and then
                        never routed: the header-suppression regex above and the
                        noindex list in SEO.tsx both already name `/onboarding`,
                        and Settings ▸ "Update My Interests" navigates here — so
                        until now that button led to the 404 page.

                        Protected, and grouped with /profile, because the page
                        reads the signed-in user and PATCHes that same user's
                        interests: signed out it has nothing to show and nothing
                        to save.

                        Deliberately NOT wired into registration. Sending every
                        new sign-up through here first is a product decision
                        about the signup flow, not a bug fix; the only thing
                        being restored here is the settings button. */}
                    <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                    <Route path="/courses" element={<CoursesListPage />} />
                    {/* Deliberately public. The player already treats the first
                        lesson of a paid course as a free preview and locks the
                        rest — but ProtectedRoute bounced signed-out visitors to
                        /auth before they ever reached it, so that preview was
                        dead code and nobody could taste before buying. Access to
                        everything past lesson one is still enforced, both here
                        and on the server. */}
                    <Route path="/course/:slug" element={<CoursePage />} />
                    <Route path="/course-details/:slug" element={<CourseViewPage />} />

                    <Route path="/cart" element={<CartPage />} />
                    {/* A private price. Deliberately NOT behind ProtectedRoute.
                        An offer can be cut for somebody who has no account —
                        frequently the reason they have not bought yet — and a
                        login wall in front of the price rebuilds the barrier the
                        offer exists to remove. The server decides who may see
                        it: bound to an account, it resolves for that account
                        alone; bound to an address, for whoever holds the link. */}
                    <Route path="/offer/:token" element={<PersonalOfferPage />} />
                    <Route path="/events" element={<EventsPage />} />
                    <Route path="/events/:slug" element={<EventDetailPage />} />
                    {/*
                      The blog is public and unauthenticated on purpose: it is the
                      only acquisition channel that keeps working after the ad
                      spend stops, and a post behind a login cannot be indexed.
                    */}
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    {/* Linked from every newsletter footer. Must never require a login. */}
                    <Route path="/unsubscribe" element={<UnsubscribePage />} />
                    <Route path="/partnerships" element={<PartnershipPage />} />
                    <Route path="/teams" element={<TeamsPage />} />
                    <Route path="/about-us" element={<AboutUs />} />
                    <Route path="/contact-us" element={<ContactUs />} />

                    {/* Verification is deliberately public — whoever is checking a
                        certificate is usually an employer with no Mindalitix account. */}
                    <Route path="/verify" element={<CertificateVerifyPage />} />
                    <Route path="/verify/:certificateNumber" element={<CertificateVerifyPage />} />
                    <Route
                      path="/certificates/:certificateId"
                      element={<ProtectedRoute><CertificatePage /></ProtectedRoute>}
                    />
                  </Route>

                  <Route
                    element={
                      <AdminProtectedRoute>
                        <AdminLayout />
                      </AdminProtectedRoute>
                    }
                  >
                    <Route path="/admin/dashboard" element={<AdminDashboard />} />
                    <Route
                      path="/admin/courses"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.COURSE_WRITE}><ViewCoursesPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/courses/create"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.COURSE_WRITE}><CreateCoursePage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/courses/edit/:courseId"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.COURSE_WRITE}><CreateCoursePage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/course-preview"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.COURSE_WRITE}><AdminCoursePreviewPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/scholarships"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.SCHOLARSHIP_WRITE}><ViewScholarshipsPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/scholarships/create"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.SCHOLARSHIP_WRITE}><CreateScholarshipPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/scholarships/edit/:scholarshipId"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.SCHOLARSHIP_WRITE}><CreateScholarshipPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/scholarships/manage/:scholarshipId"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.APPLICATION_READ}><ManageApplicantsPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/scholarship-applications"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.APPLICATION_READ}><AdminScholarshipApplicationsPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/certificate-requests"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.CERTIFICATE_READ}><CertificateRequestsPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/users"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.USER_READ}><ViewUsersPage /></AdminProtectedRoute>
                      }
                    />
                    {/* Detail page, not a nav item — AdminLayout's startsWith match keeps the "Users" title. */}
                    <Route
                      path="/admin/users/:userId"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.USER_READ}><UserProfilePage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/instructors"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.INSTRUCTOR_WRITE}><ViewInstructorsPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/instructors/create"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.INSTRUCTOR_WRITE}><AddInstructorPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/instructors/edit/:instructorId"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.INSTRUCTOR_WRITE}><AddInstructorPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/testimonials"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.TESTIMONIAL_WRITE}><ManageTestimonialsPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/testimonials/edit/:testimonialId"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.TESTIMONIAL_WRITE}><ManageTestimonialsPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/events"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.EVENT_WRITE}><ManageEvents /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/banner"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.BANNER_WRITE}><ManageBanner /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/blog"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.BLOG_WRITE}><ManageBlog /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/blog/new"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.BLOG_WRITE}><BlogEditor /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/blog/edit/:postId"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.BLOG_WRITE}><BlogEditor /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/pages"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.PAGE_WRITE}><ManagePages /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/pages/new"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.PAGE_WRITE}><PageEditor /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/pages/edit/:pageId"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.PAGE_WRITE}><PageEditor /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/comments"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.COMMENT_MODERATE}><ManageComments /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/media"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.MEDIA_READ}><MediaLibrary /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/activity"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.ACTIVITY_READ}><ActivitiesPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/notify"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.NOTIFICATION_SEND}><NotifyPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/newsletter"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.NEWSLETTER_READ}><ManageNewsletter /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/teams"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.TEAM_WRITE}><ManageTeam /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/pitch-deck"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.PITCHDECK_WRITE}><ManagePitchDeck /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/promo-codes"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.PROMO_READ}><PromoCodeManagementPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/personal-offers"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.OFFER_READ}><PersonalOffersPage /></AdminProtectedRoute>
                      }
                    />
                    {/* The course funnel: views, enrol clicks, checkouts,
                        payments, activation and abandonment, filterable by
                        campaign and lead magnet. Its own permission — it
                        carries revenue. */}
                    <Route
                      path="/admin/conversion"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.CONVERSION_READ}><ConversionPage /></AdminProtectedRoute>
                      }
                    />
                    {/* Advertising spend beside the revenue it produced, plus
                        the spend import itself. Its own permission for the same
                        reason the funnel has one, only more so — this page puts
                        cost and revenue on one screen. */}
                    <Route
                      path="/admin/paid-ads"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.PAID_ADS_READ}><PaidAdsPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/content-calendar"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.CONTENT_CALENDAR_READ}><ContentCalendarPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/affiliates"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.AFFILIATE_READ}>
                          <ManageAffiliates />
                        </AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/leads"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.LEAD_READ}><ManageLeads /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/nurture"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.LEAD_READ}><NurturePage /></AdminProtectedRoute>
                      }
                    />
                    {/* The website sales assistant: live mode, spend, transcripts. */}
                    <Route
                      path="/admin/sales-assistant"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.LEAD_READ}><SalesAssistantPage /></AdminProtectedRoute>
                      }
                    />
                    {/* The same assistant's answer bank and the questions it
                        could not answer. READ opens both lists; the page gates
                        every write on NURTURE_WRITE, as the contract does. */}
                    <Route
                      path="/admin/sales-answers"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.LEAD_READ}><SalesAnswersPage /></AdminProtectedRoute>
                      }
                    />
                    {/* The cold-calling console. READ opens the queue; the
                        page gates its own write controls on CALL_DESK_WRITE, and
                        the group manager is MANAGE-only in its own right. */}
                    <Route
                      path="/admin/call-desk"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.CALL_DESK_READ}><CallDeskPage /></AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/call-desk/groups"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.CALL_DESK_MANAGE}><CallGroupsPage /></AdminProtectedRoute>
                      }
                    />
                    {/* The shared WhatsApp inbox. READ opens it; the page gates
                        the reply box on WHATSAPP_INBOX_WRITE and the pairing
                        controls on MANAGE, because linking a number is the one
                        action here that can get the number banned. */}
                    <Route
                      path="/admin/whatsapp"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.WHATSAPP_INBOX_READ}><WhatsappInboxPage /></AdminProtectedRoute>
                      }
                    />
                    {/* The enrolment register. Read-only, and its own
                        permission rather than ENROLLMENT_WRITE: reading who
                        holds a seat is not the same act as granting one. */}
                    <Route
                      path="/admin/enrolments"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.ENROLLMENT_READ}><EnrolmentsPage /></AdminProtectedRoute>
                      }
                    />
                    {/* The abandoned-basket register. LEAD_READ, because it is
                        the lead table seen from the angle of what somebody
                        nearly bought — and the write half of the page is gated
                        separately on NURTURE_WRITE inside it, so an auditor can
                        read the list and put nobody on a phone queue. */}
                    <Route
                      path="/admin/abandoned-checkouts"
                      element={
                        <AdminProtectedRoute permission={PERMISSIONS.LEAD_READ}><AbandonedCheckoutsPage /></AdminProtectedRoute>
                      }
                    />
                    <Route path="/admin" element={<AdminHome />} />
                  </Route>


                  <Route path="/404" element={<NotFoundPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
              </div>
            </Suspense>
            {!cartpageRegex.test(pathname) && <CartSummary />}
            <SiteStructuredData />
            <CookieConsent />
          </SnackbarProvider>
        </ThemeProvider>
      </BannerProvider>
    </AuthProvider>
  );
};

export default App;